import './polyfill';

import { composeWithDevToolsDevelopmentOnly } from '@redux-devtools/extension';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { applyMiddleware, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { processRipplingData } from 'utils/util/MergeRipplingUtils';
import App from './App';
import {
  APP_INTEGRATION_POST_AUTH_PAGE,
  IDENTIFIER_FIRST_LOGIN,
  IDP_INITIATED_OIDC_FLOW,
  SESSION_ENDED,
  SESSION_FORCE_RELOGIN,
  SESSION_POST_FORCED_LOGIN,
} from './consts/consts';
import { ConfirmIntlProvider } from './locale/ConfirmIntlContext';
import reducer from './reducers';
import rootSaga from './sagas';
import * as serviceWorker from './serviceWorker';
import AuthProvider from './utils/api/AuthProvider';
import { applyKeycloakQueryOverride } from './utils/api/keycloakClient';
// all environment configurations
import config from './utils/util/config';
import AppIntegrationPostAuthPage from './views/AppIntegrations/AppIntegrationPostAuthPage';
import { ReloadAppOnNewVersion } from './views/AppVersion/ReloadAppOnNewVersion';
import ScrollToTop from './views/Layout/ScrollToTop';
import IdentifierFirstLogin from './views/Login/IdentifierFirstLogin';
import IdPInitiatedFlow from './views/Login/IdPInitiatedFlow';
import SessionForceRelogin, {
  SessionPostForcedLogin,
} from './views/Session/CustomSessionManagement';
import SessionEnded from './views/Session/SessionEnded';
import SessionSyncWatcher from './views/Session/SessionSyncWatcher';

config.init();
// Must run before anything reads isKeycloakEnabled() (AuthProvider, the
// gates in App.tsx/ProtectedRoute.tsx) so a `?keycloak=true`/`false` visit
// takes effect on this same page load, not just future ones.
applyKeycloakQueryOverride(window.location.search);

// @ts-expect-error
const tracking = config.getTracking();

const logRocketAppId = process.env.REACT_APP_LOGROCKET_APP_ID;
if (tracking.useLogRocketEventTracking && logRocketAppId) {
  LogRocket.init(logRocketAppId, {
    // @ts-expect-error
    shouldDebugLog: !config.isProduction(),
    release: process.env.REACT_APP_VERSION,
    console: {
      shouldAggregateConsoleErrors: true,
    },
    /*network: {
      responseSanitizer: (response) => {
        response.body = null;
        return response;
      },
    },
    dom: {
      inputSanitizer: true,
      textSanitizer: true,
    },*/
  });
  setupLogRocketReact(LogRocket);
}

// React Redux Saga setup
const sagaMiddleware = createSagaMiddleware();
const store = createStore(
  // @ts-expect-error
  reducer,
  composeWithDevToolsDevelopmentOnly(
    applyMiddleware(
      ...(tracking.useLogRocketEventTracking
        ? [LogRocket.reduxMiddleware()]
        : []),
      sagaMiddleware
    )
  )
);
sagaMiddleware.run(rootSaga);

const container = document.getElementById('root');
// @ts-expect-error
const root = createRoot(container);

// Merge - Rippling setup. This needs to run before Auth0 redirects the
// user and as early as possible so that the cookie is correctly set.
if (window.location.pathname === '/integrations') {
  processRipplingData(window.location.search);
}

root.render(
  <ConfirmIntlProvider>
    <Provider store={store}>
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <ReloadAppOnNewVersion />
          <SessionSyncWatcher />
          <DndProvider backend={HTML5Backend}>
            <Switch>
              <Route exact path={APP_INTEGRATION_POST_AUTH_PAGE.path}>
                <AppIntegrationPostAuthPage />
              </Route>
              <Route exact path={SESSION_FORCE_RELOGIN.path}>
                <SessionForceRelogin />
              </Route>
              <Route exact path={SESSION_POST_FORCED_LOGIN.path}>
                <SessionPostForcedLogin />
              </Route>
              <Route exact path={IDP_INITIATED_OIDC_FLOW.path}>
                <IdPInitiatedFlow />
              </Route>
              <Route exact path={IDENTIFIER_FIRST_LOGIN.path}>
                <IdentifierFirstLogin />
              </Route>
              <Route exact path={SESSION_ENDED.path}>
                <SessionEnded />
              </Route>
              <Route path="*">
                <App />
              </Route>
            </Switch>
          </DndProvider>
        </AuthProvider>
      </Router>
    </Provider>
  </ConfirmIntlProvider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
